import { UnitQueuesBundle, UnitQueueWireDto } from '@mark43/cad-api';
import { createContext, useContext, useMemo, useRef } from 'react';

type ContextType = {
    unitQueueViewsRef: React.MutableRefObject<Record<number, UnitQueueWireDto>>;
    getListeners: (() => UnitQueueViewsListener[]) | undefined;
    addListener: ((listener: UnitQueueViewsListener) => void) | undefined;
    removeListener: ((listener: UnitQueueViewsListener) => void) | undefined;
};

const UnitQueueViewsContext = createContext<ContextType>({
    unitQueueViewsRef: { current: {} },
    getListeners: undefined,
    addListener: undefined,
    removeListener: undefined,
});

export type UnitQueueViewsListener = {
    onBootstrap: () => void;
    onUpdate: (unitQueuesBundle: UnitQueuesBundle) => void;
};

export function UnitQueueViewsProvider(props: { children: React.ReactNode }) {
    const unitQueueViewsRef = useRef<ContextType['unitQueueViewsRef']['current']>({});
    const listenersRef = useRef<UnitQueueViewsListener[]>([]);
    const contextValue = useMemo(
        () => ({
            unitQueueViewsRef,
            getListeners() {
                return listenersRef.current;
            },
            addListener: (listener: UnitQueueViewsListener) => {
                if (!listenersRef.current.includes(listener)) listenersRef.current.push(listener);
                listener.onBootstrap();
            },
            removeListener: (listener: UnitQueueViewsListener) => {
                const index = listenersRef.current.indexOf(listener);
                if (index !== -1) {
                    listenersRef.current.splice(index, 1);
                }
            },
        }),
        []
    );
    return (
        <UnitQueueViewsContext.Provider value={contextValue}>
            {props.children}
        </UnitQueueViewsContext.Provider>
    );
}

export function useUnitQueueViews(): ContextType {
    return useContext(UnitQueueViewsContext);
}
